import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Check, Loader2, Calendar, Mail, Phone, MapPin, Home, Sparkles, ArrowRight } from 'lucide-react';
import FloatingContact from '../../components/public/FloatingContact';
import { publicGet } from '../../lib/publicApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ConfirmationPage() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicGet(`/api/guest/booking/${bookingId}`)
      .then(setBooking)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [bookingId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0C0B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-[#0D0C0B] flex flex-col items-center justify-center p-6 text-center">
        <p className="overline mb-4">Erreur</p>
        <h1 className="font-display text-5xl mb-6">Réservation introuvable</h1>
        <Link to="/" className="btn-luxury-outline">Retour à l'accueil</Link>
      </div>
    );
  }

  const isPaid = booking.payment_status === 'paid';
  const isDayuse = booking.session_type === 'dayuse';

  return (
    <div className="min-h-screen bg-[#0D0C0B] text-[#F5F2EB] flex items-center justify-center p-6 grain" data-testid="confirmation-page">
      <div className="max-w-2xl w-full text-center animate-fade-in">
        <div className="w-20 h-20 mx-auto mb-10 border border-[#D4AF37] flex items-center justify-center gold-glow">
          <Check className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} />
        </div>

        <p className="overline mb-4">{isPaid ? 'Paiement confirmé' : 'Réservation enregistrée'}</p>
        <h1 className="font-display text-5xl md:text-6xl font-light leading-tight mb-6">
          Merci, <span className="italic text-[#D4AF37]">{booking.guest_name?.split(' ')[0]}</span>
        </h1>
        <p className="font-body font-light text-[#A39E93] mb-12 max-w-md mx-auto">
          Votre instant Era Lodge est réservé. Vous recevrez une confirmation détaillée par email dans quelques instants.
        </p>

        <div className="border border-white/10 p-8 md:p-10 text-left mb-10" data-testid="confirmation-summary">
          <div className="flex items-center justify-between mb-8">
            <p className="overline">Référence</p>
            <p className="font-mono text-[#D4AF37] text-sm tracking-wider">{booking.id?.slice(0, 8).toUpperCase()}</p>
          </div>

          <div className="luxury-divider mb-8" />

          <ul className="space-y-5 text-sm font-body font-light">
            <li className="flex items-start gap-4">
              <Home className="w-4 h-4 text-[#D4AF37] mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="overline mb-1">Suite</p>
                <p className="text-base text-[#F5F2EB]">{booking.property_name}</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Calendar className="w-4 h-4 text-[#D4AF37] mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="overline mb-1">{isDayuse ? 'Day Use' : 'Séjour'}</p>
                <p className="text-base text-[#F5F2EB]">
                  {format(parseISO(booking.check_in), 'EEEE d MMMM yyyy', { locale: fr })}
                  {!isDayuse && (
                    <>
                      <span className="text-[#A39E93]"> au </span>
                      {format(parseISO(booking.check_out), 'EEEE d MMMM yyyy', { locale: fr })}
                    </>
                  )}
                </p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Mail className="w-4 h-4 text-[#D4AF37] mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="overline mb-1">Contact</p>
                <p className="text-base text-[#F5F2EB]">{booking.guest_email}</p>
                <p className="text-sm text-[#A39E93]">{booking.guest_phone}</p>
              </div>
            </li>
            {booking.selected_options?.length > 0 && (
              <li className="flex items-start gap-4">
                <Sparkles className="w-4 h-4 text-[#D4AF37] mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="overline mb-1">Rituels choisis</p>
                  <p className="text-base text-[#F5F2EB]">
                    {booking.selected_options.map(o => o.name).join(' · ')}
                  </p>
                </div>
              </li>
            )}
          </ul>

          <div className="luxury-divider my-8" />

          <div className="flex items-baseline justify-between">
            <span className="overline">Total {isPaid ? 'payé' : 'à régler'}</span>
            <span className="font-display text-4xl text-[#D4AF37] tabular-nums">€{booking.total_price?.toFixed(2)}</span>
          </div>
          {!isPaid && booking.payment_method && (
            <p className="mt-4 text-xs text-[#A39E93] font-body font-light text-right uppercase tracking-[0.18em]">
              {booking.payment_method === 'cash' && 'Paiement à votre arrivée'}
              {booking.payment_method === 'wero' && 'Paiement Wero – instructions par email'}
              {booking.payment_method === 'paypal' && 'Paiement PayPal en attente'}
            </p>
          )}
        </div>

        <Link to="/" className="btn-luxury-outline" data-testid="back-home-btn">
          Retour à l'accueil <ArrowRight className="ml-3 w-4 h-4" />
        </Link>
      </div>
      <FloatingContact />
    </div>
  );
}
